"use strict";

/* ------------------------------------------------------------------ *
 * Mapper 17: Bandai FCG with 8 KB WRAM (Bandai FG-1)
 * ------------------------------------------------------------------ *
 * Used by Tsuppari Oozumou, Bakushou!! Jinsei Gekijou, etc.
 *
 * CPU $6000-$7FFF: 8 KB battery-backed WRAM
 * CPU $8000-$BFFF: 16 KB switchable PRG bank
 * CPU $C000-$FFFF: 16 KB fixed to last bank
 * PPU $0000-$1FFF: eight 1 KB switchable CHR banks
 *
 * Registers at $8000-$FFFF (one per $1000 block, like LZ93D50):
 *   $8000-$8007: CHR banks 0-7
 *   $8008: PRG bank select (low 4 bits)
 *   $8009: Mirroring (0=V, 1=H, 2/3=single screen low/high)
 *   $800A: IRQ enable + reload from latch (bit 0)
 *   $800B: IRQ latch low byte
 *   $800C: IRQ latch high byte
 *   $800D-$800F: unused
 *
 * IRQ: 16-bit down-counter clocked by CPU cycles (M2).
 * Write to $800A enables counting and reloads counter from latch.
 * IRQ fires when counter reaches 0, stays asserted until $800A write.
 * ------------------------------------------------------------------ */

var CHR_WINDOWS = [
  0x0000, 0x0400, 0x0800, 0x0c00, 0x1000, 0x1400, 0x1800, 0x1c00,
];

function register(Mappers) {
  Mappers[17] = function (nes) {
    this.nes = nes;
    this.wram = new Uint8Array(0x2000);
  };

  Mappers[17].prototype = new Mappers[0]();

  // IRQ counter runs on CPU cycles
  Mappers[17].prototype.watchesCpuCycles = true;

  Mappers[17].prototype.reset = function () {
    Mappers[0].prototype.reset.apply(this);

    this.chrBanks = [0, 0, 0, 0, 0, 0, 0, 0];
    this.prgBank = 0;

    this.mirroring = this.nes.rom ? this.nes.rom.mirroring : 0;
    this.applyMirroring();

    this.irqEnabled = false;
    this.irqCounter = 0;
    this.irqLatch = 0;
    this.irqAsserted = false;
  };

  Mappers[17].prototype.load = function (address) {
    address &= 0xffff;

    if (address >= 0x6000 && address < 0x8000) {
      return this.wram[address - 0x6000];
    }

    return Mappers[0].prototype.load.apply(this, arguments);
  };

  Mappers[17].prototype.write = function (address, value) {
    if (address >= 0x6000 && address < 0x8000) {
      // 8 KB WRAM at $6000-$7FFF
      this.wram[address - 0x6000] = value & 0xff;
      this.saveWramByte(address - 0x6000, value & 0xff);
      return;
    }

    if (address >= 0x8000) {
      // Registers at $8000-$FFFF, decoded by low 4 bits of address
      this.writeRegister(address & 0x0f, value);
      return;
    }

    Mappers[0].prototype.write.apply(this, arguments);
  };

  Mappers[17].prototype.writeRegister = function (reg, value) {
    if (reg <= 0x07) {
      // $8000-$8007: eight 1 KB CHR banks
      this.chrBanks[reg] = value & 0xff;
      this.load1kVromBank(this.chrBanks[reg], CHR_WINDOWS[reg]);
      return;
    }

    switch (reg) {
      case 0x08:
        // $8008: PRG bank select (4 bits) for $8000-$BFFF
        this.prgBank = value & 0x0f;
        this.loadRomBank(this.prgBank, 0x8000);
        return;

      case 0x09:
        // $8009: Mirroring
        this.mirroring = value & 0x03;
        this.applyMirroring();
        return;

      case 0x0a:
        // $800A: IRQ enable (bit 0). Write acknowledges IRQ and reloads counter from latch.
        this.irqEnabled = (value & 0x01) !== 0;
        if (this.irqEnabled) {
          this.irqCounter = this.irqLatch;
        }
        this.irqAsserted = false;
        return;

      case 0x0b:
        // $800B: IRQ latch low byte
        this.irqLatch = (this.irqLatch & 0xff00) | (value & 0xff);
        return;

      case 0x0c:
        // $800C: IRQ latch high byte
        this.irqLatch = (this.irqLatch & 0x00ff) | ((value & 0xff) << 8);
        return;

      case 0x0d:
      case 0x0e:
      case 0x0f:
        // $800D-$800F: unused
        return;
    }
  };

  Mappers[17].prototype.applyMirroring = function () {
    switch (this.mirroring & 0x03) {
      case 0:
        this.setMirroring(this.nes.rom.VERTICAL_MIRRORING);
        break;
      case 1:
        this.setMirroring(this.nes.rom.HORIZONTAL_MIRRORING);
        break;
      case 2:
        this.setMirroring(this.nes.ppu.MIRRORING_TYPES.SINGLE_SCREEN);
        break;
      default:
        this.setMirroring(this.nes.ppu.MIRRORING_TYPES.SINGLE_SCREEN_ALT);
        break;
    }
  };

  Mappers[17].prototype.saveWramByte = function (offset, value) {
    var ram = this.nes.rom.batteryRamData;
    if (ram !== null && ram !== undefined && offset < ram.length) {
      ram[offset] = value;
    }
    if (typeof this.nes.opts.onBatteryRamWrite === "function") {
      this.nes.opts.onBatteryRamWrite(0x6000 + offset, value);
    }
  };

  Mappers[17].prototype.loadBatteryRam = function () {
    var ram = this.nes.rom.batteryRamData;
    if (ram === null || ram === undefined) {
      return;
    }
    var count = Math.min(this.wram.length, ram.length);
    for (var i = 0; i < count; i++) {
      this.wram[i] = ram[i];
    }
  };

  // IRQ counter: decremented each CPU cycle. IRQ fires when counter is 0 at
  // start of cycle (Mesen-compatible behavior).
  Mappers[17].prototype.clockCpuCycles = function (cycles) {
    if (this.irqAsserted) {
      this.nes.cpu.requestIrq(this.nes.cpu.IRQ_NORMAL);
    }

    if (!this.irqEnabled) {
      return;
    }

    for (var i = 0; i < cycles; i++) {
      if (this.irqCounter === 0) {
        this.irqAsserted = true;
        this.nes.cpu.requestIrq(this.nes.cpu.IRQ_NORMAL);
      }
      this.irqCounter = (this.irqCounter - 1) & 0xffff;
    }
  };

  Mappers[17].prototype.loadROM = function () {
    if (!this.nes.rom.valid) {
      throw new Error("Bandai FCG 17: Invalid ROM! Unable to load.");
    }

    this.reset();
    this.loadCHRROM();
    this.loadBatteryRam();
    this.loadTrainer();

    // Power-on: CHR banks at 0, PRG bank 0 at $8000, last bank at $C000
    for (var i = 0; i < 8; i++) {
      this.load1kVromBank(0, CHR_WINDOWS[i]);
    }
    this.loadRomBank(0, 0x8000);
    this.loadRomBank(this.nes.rom.romCount - 1, 0xc000);
    this.applyMirroring();

    this.resetIrqHandler();
  };

  Mappers[17].prototype.toJSON = function () {
    var s = Mappers[0].prototype.toJSON.apply(this);
    s.chrBanks = this.chrBanks.slice();
    s.prgBank = this.prgBank;
    s.mirroring = this.mirroring;
    s.irqEnabled = this.irqEnabled;
    s.irqCounter = this.irqCounter;
    s.irqLatch = this.irqLatch;
    s.irqAsserted = this.irqAsserted;
    s.wram = Array.from(this.wram);
    return s;
  };

  Mappers[17].prototype.fromJSON = function (s) {
    Mappers[0].prototype.fromJSON.apply(this, arguments);

    this.chrBanks = [0, 0, 0, 0, 0, 0, 0, 0];
    if (s.chrBanks) {
      for (var i = 0; i < 8; i++) {
        this.chrBanks[i] = s.chrBanks[i] || 0;
      }
    }
    this.prgBank = s.prgBank || 0;
    this.mirroring = s.mirroring || 0;
    this.irqEnabled = s.irqEnabled === true;
    this.irqCounter = s.irqCounter || 0;
    this.irqLatch = s.irqLatch || 0;
    this.irqAsserted = s.irqAsserted === true;

    if (s.wram) {
      for (var i = 0; i < this.wram.length; i++) {
        this.wram[i] = s.wram[i] || 0;
      }
    }

    for (var i = 0; i < 8; i++) {
      this.load1kVromBank(this.chrBanks[i], CHR_WINDOWS[i]);
    }
    this.loadRomBank(this.prgBank, 0x8000);
    this.loadRomBank(this.nes.rom.romCount - 1, 0xc000);
    this.applyMirroring();
  };
}

module.exports = { register: register };
